import { Flags } from '@fullstory/client-api';
import {
  AccessResponse,
  CanAccessRequest,
  CanAccessResponse,
} from '@fullstory/proto/fs/shared/accesscontrol';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createRequestReducer } from '@fullstory/redux-util';
import {
  DispatchedAction,
  FailureAction,
  RequestLifecycleAction,
  RequestState,
  SuccessAction,
} from '@fullstory/redux-util/types';
import { AppDispatch, AppState } from '../types';
import { int64 } from '@fullstory/proto/google/protobuf';
import { getOrgId } from '../modules/fullstory/selectors';

export type Access = { [key: string]: AccessResponse };
export type AccessReducerState = {
  access: Access | undefined;
  canCreate: boolean | undefined;
};

export const defaultState: RequestState<AccessReducerState> = {
  lastRequested: -1,
  lastResponded: -1,
  loading: false,
  error: false,
  data: null,
  safeMessage: null,
};

/** Generic helper to create an access control reducer. */
export function createAccessReducer<R extends string, S extends string, F extends string>(
  requestType: R,
  successType: S,
  failureType: F,
) {
  const reqReducer = createRequestReducer<CanAccessResponse>()(
    requestType,
    successType,
    failureType,
  );

  return function (
    state: RequestState<AccessReducerState> = defaultState,
    action: DispatchedAction<R, S, F, CanAccessRequest, CanAccessResponse>,
  ) {
    const { data, ...metadata } = reqReducer({ ...state, data: null }, action);
    switch (action.type) {
      case requestType:
      case failureType: {
        const { error } = action as FailureAction<F, CanAccessRequest>;
        return {
          ...metadata,
          data: state.data,
          safeMessage: error?.safeMessage,
        };
      }
      case successType: {
        const { data, request } = action as SuccessAction<S, CanAccessRequest, CanAccessResponse>;
        const ids = (request.body?.stringIds || request.body?.int64Ids)?.ids;

        if (!ids && data.response[0]) {
          return {
            ...metadata,
            data: {
              ...state.data,
              canCreate: data.response[0].canCreate,
              access: state.data?.access ?? defaultState.data?.access,
            },
            safeMessage: null,
          };
        } else if (!ids) {
          return state;
        }

        return {
          ...metadata,
          data: {
            ...state.data,
            canCreate: state.data?.canCreate,
            access: data.response.reduce(
              (all, access, idx) => ({
                ...all,
                [ids[idx] as string]: access,
              }),
              state.data?.access ?? defaultState.data?.access,
            ),
          },
          safeMessage: null,
        };
      }
    }
    return state;
  };
}

type accessRequest =
  | ((
      body: CanAccessRequest,
    ) => RequestLifecycleAction<
      string,
      string,
      string,
      CanAccessRequest,
      CanAccessResponse,
      CanAccessResponse
    >)
  | ((
      body: CanAccessRequest,
      flags: Flags,
    ) => RequestLifecycleAction<
      string,
      string,
      string,
      CanAccessRequest,
      CanAccessResponse,
      CanAccessResponse
    >);
export type IDs = Pick<CanAccessRequest, 'stringIds' | 'int64Ids'> | undefined;

const dispatchAccessRequest = (
  request: accessRequest,
  dispatch: AppDispatch,
  orgId: string,
  ids: IDs,
  flags: Flags,
) =>
  dispatch(
    request(
      {
        orgId,
        ids: [],
        ...ids,
      },
      flags,
    ),
  );

// Generic hook to control dispatching the access requests based on a set of IDs
// Separated for more advanced usecases that want more control over the requests.
export function makeUseDispatchAccess(request: accessRequest) {
  return (
    ids: IDs,
    orgId: string,
    flags: Flags,
    allowNoIds: boolean = false,
    dispatchReq: boolean = true,
  ) => {
    const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
      const allIds = ((ids?.stringIds || ids?.int64Ids)?.ids || []).map((id: string | int64) =>
        id.toString(),
      );

      if (dispatchReq && (allowNoIds || allIds.length > 0)) {
        dispatchAccessRequest(request, dispatch, orgId, ids, flags);
      }
    }, [ids, dispatch, orgId, flags, allowNoIds, dispatchReq]);
  };
}

/** Generic hook to fetch access controls for a single unit. The ids array MUST BE memoized.
 * If no ids are provided, the request will not be dispatched */
export function makeUseAccess(
  request: accessRequest,
  selector: (state: AppState) => RequestState<AccessReducerState>,
) {
  const useDispatchAccess = makeUseDispatchAccess(request);
  return (ids: IDs, orgId: string, flags: Flags): RequestState<AccessReducerState> => {
    useDispatchAccess(ids, orgId, flags, false);
    const state = useSelector(selector);
    return state;
  };
}

/** Generic hook to check if the user has access to create a new object. Will dispatch without any ids. */
export const makeUseCreateAccess = (
  request: accessRequest,
  selector: (state: AppState) => RequestState<AccessReducerState>,
) => {
  const useDispatchAccess = makeUseDispatchAccess(request);
  return (orgId: string, flags: Flags, dispatch: boolean = true): boolean => {
    useDispatchAccess(undefined, orgId, flags, true, dispatch);
    const state = useSelector(selector);
    return state.data?.canCreate ?? false;
  };
};

/**
 * Gets the create access an object type, fetches if needed.
 * Intended for use when the response needs to be awaited.
 */
export const useGetCreateAccess = (request: accessRequest, flags: Flags) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  const getAccess = useCallback(async (): Promise<boolean> => {
    const rsp: CanAccessResponse = await dispatchAccessRequest(
      request,
      dispatch,
      orgId,
      undefined,
      flags,
    );

    return rsp.response[0]?.canCreate ?? false;
  }, [dispatch, flags, orgId, request]);

  return getAccess;
};

/**
 * Gets the access data for an object, fetches if needed.
 * Intended for use when the id of an object is not known in advance
 * and the response needs to be awaited.
 */
export const useGetAccess = (request: accessRequest, flags: Flags) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  const getAccess = useCallback(
    async (ids: IDs): Promise<{ [id: string]: AccessResponse }> => {
      const allIds = ((ids?.stringIds || ids?.int64Ids)?.ids || []).map((id: string | int64) =>
        id.toString(),
      );

      if (!allIds.length) {
        return {};
      }

      const rsp: CanAccessResponse = await dispatchAccessRequest(
        request,
        dispatch,
        orgId,
        ids,
        flags,
      );

      return rsp.response.reduce(
        (acc, access, idx) => ({
          ...acc,
          [allIds[idx] as string]: access,
        }),
        {},
      );
    },
    [dispatch, flags, orgId, request],
  );

  return getAccess;
};

function validIDs<T>(ids: T[] | undefined): ids is T[] {
  if (!ids) {
    return false;
  }
  return ids.filter(id => id !== null && id !== undefined).length > 0;
}

export function stringIDs(ids: string[] | undefined): IDs {
  if (!validIDs(ids)) {
    return undefined;
  }
  return { stringIds: { ids } };
}

export function int64IDs(ids: int64[] | undefined): IDs {
  if (!validIDs(ids)) {
    return undefined;
  }
  return { int64Ids: { ids } };
}

export const defaultFullAccess = {
  owners: [],
  id: '',
  canRead: true,
  canUpdate: true,
  canDelete: true,
  canCreate: true,
};

export const defaultNoAccess = {
  owners: [],
  id: '',
  canRead: false,
  canUpdate: false,
  canDelete: false,
  canCreate: false,
};
